import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { SlDialog } from '@shoelace-style/shoelace';
import ActionWrap from 'dashboard/components/action-wrap';
import Button from 'dashboard/components/button';
import Divider from 'dashboard/components/divider';
import trackEvent from 'dashboard/helpers/track-event';
import NotifyService, { FeedbackModalType } from 'dashboard/services/notify';
import { t, type IntlService } from 'ember-intl';
import Portal from 'ember-stargate/components/portal';
import { eq, or } from 'ember-truth-helpers';

interface FeedbackDialogSignature {
  Element: SlDialog;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    FeedbackDialog: typeof FeedbackDialog;
  }
}

export default class FeedbackDialog extends Component<FeedbackDialogSignature> {
  @service declare intl: IntlService;
  @service declare notify: NotifyService;

  types = FeedbackModalType;

  get title() {
    const count = this.notify.intlArgs?.count;
    switch (this.notify.feedbackType) {
      case FeedbackModalType.ACTIVATE_TEACHER_CONFIRMATION: // fallthrough
      case FeedbackModalType.ACTIVATE_STUDENT_CONFIRMATION:
        return this.intl.t('Reactivate Account', { count });
      case FeedbackModalType.REMOVE_TEACHER_CONFIRMATION: // fallthrough
      case FeedbackModalType.DEACTIVATE_STUDENT_CONFIRMATION:
        return this.intl.t('Deactivate Accounts', { count });
      case FeedbackModalType.LICENSE_UPGRADE_EXCEPTION:
        return this.intl.t('Not Enough Licenses');
      case FeedbackModalType.DOWNGRADE_TEACHER_LICENSE:
        return this.intl.t('Downgrade to Basic');
      case FeedbackModalType.SIGN_IN_QR_EXPORT_CONFIRMATION:
        return this.intl.t('Enable QR Codes?');
      case FeedbackModalType.SIGN_IN_QR_EXPORT_ERROR:
        return this.intl.t('Error Getting QR Codes');
      case FeedbackModalType.START_SYNC_DUPLICATE_EMAILS_ERROR:
        return this.intl.t('Duplicate Emails Found');
      case FeedbackModalType.START_SYNC_ALREADY_RUNNING_ERROR:
      case FeedbackModalType.START_SYNC_GENERIC_ERROR:
        return this.intl.t('Error Starting Sync');
      case FeedbackModalType.GENERIC_ERROR:
        return this.intl.t('Error');
      default:
        return '';
    }
  }

  get message(): string[] {
    const teacherCount = this.notify.intlArgs?.count;
    const school = this.notify.intlArgs?.school;

    let output;

    switch (this.notify.feedbackType) {
      case FeedbackModalType.ACTIVATE_TEACHER_CONFIRMATION:
        output = [
          this.intl.t(
            'Reactivating this user’s account will restore their ability to sign into their Showbie account.'
          ),
          this.intl.t(
            'Any licenses previously allocated to this user will need to be applied again.'
          ),
        ];
        break;
      case FeedbackModalType.REMOVE_TEACHER_CONFIRMATION:
        output = [
          this.intl.t(
            'Deactivating a teacher’s account removes their access to Showbie.'
          ),
          this.intl.t(
            'If the teacher has left the school, reassign or archive their active classes so student class lists remain up-to-date.'
          ),
        ];
        break;
      case FeedbackModalType.ACTIVATE_STUDENT_CONFIRMATION:
        output = [
          this.intl.t(
            'Reactivating this student’s account will restore their ability to sign into their Showbie account.'
          ),
          this.intl.t(
            'Unless the student has been blocked, they will regain access to all their classes and assignments.'
          ),
        ];
        break;
      case FeedbackModalType.DEACTIVATE_STUDENT_CONFIRMATION:
        output = [
          this.intl.t(
            'Deactivating a student’s account removes their access to Showbie.'
          ),
          this.intl.t(
            'Their profile will be marked as deactivated, but student content will remain visible unless they are blocked from classes and groups.'
          ),
        ];
        break;
      case FeedbackModalType.LICENSE_UPGRADE_EXCEPTION:
        output = this.intl.t(
          'Oops! {schoolName} does not have enough licenses available.',
          { schoolName: school }
        );
        break;
      case FeedbackModalType.DOWNGRADE_TEACHER_LICENSE:
        output = `${this.intl.t(
          'Are you sure you want to downgrade {count} teachers to Basic?',
          { count: teacherCount }
        )} ${this.intl.t(
          'These teachers and their students will no longer have access to Showbie Pro features.',
          { count: teacherCount }
        )}`;
        break;
      case FeedbackModalType.SIGN_IN_QR_EXPORT_ERROR:
        output = this.intl.t(
          'There was a problem exporting your QR Codes. Please try again. Contact support if the issue continues.'
        );
        break;
      case FeedbackModalType.START_SYNC_ALREADY_RUNNING_ERROR:
        output = this.intl.t(
          'A sync is currently in progress. Try again after the current sync is complete.'
        );
        break;
      case FeedbackModalType.START_SYNC_GENERIC_ERROR:
      case FeedbackModalType.GENERIC_ERROR:
        output = this.intl.t('An error has occurred. Try again later.');
        break;
      default:
        output = '';
    }
    return typeof output === 'string' ? [output] : output;
  }

  <template>
    <Portal @target='feedback-dialog'>
      <sl-dialog
        {{! template-lint-disable no-inline-styles }}
        class='shadow-dialog'
        open={{this.notify.showFeedback}}
        style='--width: 25rem; --body-spacing: 1.5rem'
        no-header
        label={{this.title}}
        data-test-feedback-dialog
        ...attributes
        {{on 'sl-hide' this.notify.closeFeedback}}
      >
        <h2
          class='font-extrabold leading-loose pb-6 text-center text-lg'
          data-test-feedback-dialog-title
        >
          {{this.title}}
        </h2>
        <div class='-mx-6'>
          <Divider />
        </div>
        <div
          class='text-md leading-loose text-center'
          data-test-feedback-dialog-message
        >
          {{! Capturing specific states here that need to use ActionWrap }}
          {{#if
            (eq
              this.notify.feedbackType this.types.SIGN_IN_QR_EXPORT_CONFIRMATION
            )
          }}
            <p class='pt-4'>{{t
                'Performing this action will enable QR Codes for all students.'
              }}</p>
            <p class='pt-4'>
              <ActionWrap
                @string={{t
                  'QR codes provide direct access to student accounts and should be used with _security best-practices_ in mind.'
                }}
                @href='https://support.showbie.com/en/articles/7988357-security-best-practices-for-your-showbie-account'
              /></p>
          {{else if
            (eq
              this.notify.feedbackType
              this.types.START_SYNC_DUPLICATE_EMAILS_ERROR
            )
          }}
            <p class='pt-4'>
              <ActionWrap
                @string={{t
                  'We found accounts using the same email address set to _Create new account._ All Showbie accounts must have a unique email address.'
                }}
                as |text|
              ><em><strong>{{text}}</strong></em></ActionWrap>
            </p>
            <p class='pt-4'>{{t
                'To resolve this issue, set one or more of these accounts to "Do not sync."'
              }}
            </p>
          {{else}}
            {{#each this.message as |paragraph|}}
              <p class='pt-4'>{{paragraph}}</p>
            {{/each}}
          {{/if}}
        </div>
        <div slot='footer' class='gap-4 grid'>
          {{#if
            (eq
              this.notify.feedbackType this.types.ACTIVATE_TEACHER_CONFIRMATION
            )
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-reactivate-teacher-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Reactivate Account' count=this.notify.intlArgs.count}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq this.notify.feedbackType this.types.REMOVE_TEACHER_CONFIRMATION)
          }}
            <Button
              @tone='critical'
              @size='large'
              data-test-feedback-dialog-remove-teachers-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Deactivate Accounts' count=this.notify.intlArgs.count}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq
              this.notify.feedbackType this.types.ACTIVATE_STUDENT_CONFIRMATION
            )
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-reactivate-student-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Reactivate Account' count=this.notify.intlArgs.count}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq
              this.notify.feedbackType
              this.types.DEACTIVATE_STUDENT_CONFIRMATION
            )
          }}
            <Button
              @tone='critical'
              @size='large'
              data-test-feedback-dialog-deactivate-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Deactivate Accounts' count=this.notify.intlArgs.count}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq this.notify.feedbackType this.types.DOWNGRADE_TEACHER_LICENSE)
          }}
            <Button
              @tone='critical'
              @size='large'
              data-test-feedback-dialog-downgrade-teachers-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Downgrade Licenses' count=this.notify.intlArgs.count}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq this.notify.feedbackType this.types.LICENSE_UPGRADE_EXCEPTION)
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-buy-more-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Buy More'}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Close'}}
            </Button>
          {{else if
            (eq
              this.notify.feedbackType this.types.SIGN_IN_QR_EXPORT_CONFIRMATION
            )
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-qr-export-button
              {{on 'click' (trackEvent 'students__qr_codes_exported')}}
              {{on 'click' this.notify.performAction}}
            >
              {{t 'OK'}}
            </Button>
            <Button
              @variant='muted'
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'Cancel'}}
            </Button>
          {{else if
            (eq this.notify.feedbackType this.types.SIGN_IN_QR_EXPORT_ERROR)
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-close-button
              {{on 'click' this.notify.closeFeedback}}
            >
              {{t 'OK'}}
            </Button>
          {{else if
            (eq
              this.notify.feedbackType
              this.types.START_SYNC_DUPLICATE_EMAILS_ERROR
            )
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-review-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'Review Accounts'}}
            </Button>
          {{else if
            (or
              (eq this.notify.feedbackType this.types.START_SYNC_GENERIC_ERROR)
              (eq
                this.notify.feedbackType
                this.types.START_SYNC_ALREADY_RUNNING_ERROR
              )
              (eq this.notify.feedbackType this.types.GENERIC_ERROR)
            )
          }}
            <Button
              @size='large'
              data-test-feedback-dialog-dismiss-error-button
              {{on 'click' this.notify.performAction}}
            >
              {{t 'OK'}}
            </Button>
          {{/if}}
        </div>
      </sl-dialog>
    </Portal>
  </template>
}
